import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon, ArrowTopRightOnSquareIcon, ListBulletIcon, PencilSquareIcon, PlusIcon,
  Squares2X2Icon, TrashIcon,
} from '@heroicons/react/24/outline';
import {
  getNodes, registerNode, updateNode, deleteNode,
  getNodeCameras, getNodeActiveAlertSummary,
} from '../services/api.service';
import {
  PageTitle, PageWithFilters, FilterColumn, FilterSection, FilterCheck, FilterSearch, useFilterColumn,
  isCameraOnline, RESULTS_CARD, TABLE, THEAD_ROW, TH, TD, ROW,
} from '@trinetra/shared';
import { useAuth } from '../contexts/AuthContext';
import { useNode } from '../contexts/NodeContext';

/**
 * Normalise the node-proxy's response shapes to an array. Different endpoints
 * return a bare array, `{ data: [...] }`, or `{ data: [...], pagination }`.
 */
function toArray(res: any): any[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

/**
 * Soft tinted background + solid text from a CSS-var colour. The theme's colours are bare
 * `var()`s, so Tailwind opacity modifiers (`bg-success/15`) compile to nothing — color-mix
 * is the working equivalent.
 */
const tint = (c: string) => ({
  backgroundColor: `color-mix(in srgb, ${c} 15%, transparent)`,
  color: c,
});

function formatLastSeen(ts: number): string {
  if (!ts) return 'never';
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

interface NodeInfo {
  nodeId: string;
  name: string;
  status: 'online' | 'offline';
  tunnelActive: boolean;
  lastSeen: number;
  metrics: any;
  version: string;
  capabilities: string[];
  activeSessions: number;
}

/**
 * The two figures each site's row shows. `null` = not read (offline, no permission, or the
 * call failed) and renders as "—"; it never means zero.
 */
interface SiteStats {
  cameras: { online: number; total: number } | null;
  alerts: { total: number; critical: number } | null;
  loading: boolean;
}

type ViewMode = 'list' | 'cards';
type StatusFilter = 'all' | 'online' | 'offline';
type SortKey = 'status' | 'name' | 'alerts' | 'lastSeen';

const VIEW_KEY = 'trinetra.dashboard.sitesView';

const SORTS: { key: SortKey; label: string }[] = [
  { key: 'status', label: 'Status' },
  { key: 'name', label: 'Name' },
  { key: 'alerts', label: 'Active alerts' },
  { key: 'lastSeen', label: 'Last seen' },
];

const needsAttention = (s?: SiteStats) =>
  !!s && ((s.alerts?.total ?? 0) > 0 || (s.cameras ? s.cameras.total - s.cameras.online > 0 : false));

export default function Dashboard() {
  const [nodes, setNodes] = useState<NodeInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerForm, setRegisterForm] = useState({ nodeId: '', name: '', authToken: '' });
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');

  // Edit state
  const [editNode, setEditNode] = useState<NodeInfo | null>(null);
  const [editForm, setEditForm] = useState({ name: '', authToken: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<NodeInfo | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [stats, setStats] = useState<Record<string, SiteStats>>({});
  // Bumped by Refresh and the 30s timer so the per-site figures re-read even when the node
  // list itself hasn't changed.
  const [statsTick, setStatsTick] = useState(0);

  // View + filters
  const [view, setView] = useState<ViewMode>(() => {
    try { return localStorage.getItem(VIEW_KEY) === 'cards' ? 'cards' : 'list'; } catch { return 'list'; }
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('status');
  const filterCol = useFilterColumn('all-sites-dashboard');

  const { hasPermission } = useAuth();
  const { selectNode } = useNode();
  const navigate = useNavigate();
  const canAlerts = hasPermission('alerts.view');
  const canEdit = hasPermission('nodes.edit');
  const canDelete = hasPermission('nodes.delete');

  const changeView = (v: ViewMode) => {
    setView(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* private window */ }
  };

  /**
   * Select the site BEFORE navigating: /site-dashboard reads the selected node, so routing
   * first would render whichever site (or none) was selected before.
   */
  const openSite = (node: NodeInfo) => {
    selectNode(node.nodeId);
    navigate('/site-dashboard');
  };

  const fetchNodes = async () => {
    try {
      const data = await getNodes();
      setNodes(data.nodes || []);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to fetch sites');
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => {
    fetchNodes();
    setStatsTick((t) => t + 1);
  };

  useEffect(() => {
    fetchNodes();
    // NodeContext already polls getNodes() every 10s globally; this slower refresh keeps the
    // page's own list and per-site figures current.
    const interval = setInterval(() => {
      if (!document.hidden) refresh();
    }, 30_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Light per-site fan-out: the camera list (for online/total) and the active-alert summary,
   * for online sites only, BATCH_SIZE sites at a time so a large estate doesn't open dozens of
   * tunnel calls at once. Each read is settled on its own — a failure blanks that one cell.
   */
  useEffect(() => {
    const onlineNodes = nodes.filter((n) => n.status === 'online');
    if (onlineNodes.length === 0) return;
    let cancelled = false;

    // Skeletons only for sites we have nothing for yet, so a refresh doesn't flash.
    setStats((prev) => {
      const next = { ...prev };
      for (const n of onlineNodes) {
        if (!next[n.nodeId]) next[n.nodeId] = { cameras: null, alerts: null, loading: true };
      }
      return next;
    });

    (async () => {
      const BATCH_SIZE = 5;
      for (let i = 0; i < onlineNodes.length; i += BATCH_SIZE) {
        if (cancelled) return;
        const results: Record<string, SiteStats> = {};
        await Promise.allSettled(
          onlineNodes.slice(i, i + BATCH_SIZE).map(async (node) => {
            const [cams, alerts] = await Promise.allSettled([
              getNodeCameras(node.nodeId),
              canAlerts ? getNodeActiveAlertSummary(node.nodeId) : Promise.reject(new Error('no permission')),
            ]);
            let cameras: SiteStats['cameras'] = null;
            if (cams.status === 'fulfilled') {
              const list = toArray(cams.value);
              cameras = { online: list.filter((c: any) => isCameraOnline(c?.status)).length, total: list.length };
            }
            const summary = alerts.status === 'fulfilled' ? alerts.value : null;
            results[node.nodeId] = {
              cameras,
              alerts: summary ? { total: Number(summary.total) || 0, critical: Number(summary.critical) || 0 } : null,
              loading: false,
            };
          }),
        );
        // Commit each batch as it lands so early rows fill in while later ones fetch.
        if (!cancelled) setStats((prev) => ({ ...prev, ...results }));
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes.map((n) => n.nodeId + n.status).join(','), canAlerts, statsTick]);

  /* ─── Filtering ─── */

  const onlineCount = nodes.filter((n) => n.status === 'online').length;

  // Search + attention first; the status counts are taken from this so they add up to what
  // the other filters leave.
  const preStatus = useMemo(() => {
    const q = search.trim().toLowerCase();
    return nodes.filter((n) => {
      if (q && ![n.name, n.nodeId, n.version].some((v) => (v || '').toLowerCase().includes(q))) return false;
      if (attentionOnly && !needsAttention(stats[n.nodeId])) return false;
      return true;
    });
  }, [nodes, search, attentionOnly, stats]);

  const statusCounts = {
    all: preStatus.length,
    online: preStatus.filter((n) => n.status === 'online').length,
    offline: preStatus.filter((n) => n.status !== 'online').length,
  };
  const attentionCount = nodes.filter((n) => needsAttention(stats[n.nodeId])).length;

  const visible = useMemo(() => {
    const list = preStatus.filter((n) => statusFilter === 'all' || (statusFilter === 'online') === (n.status === 'online'));
    const byName = (a: NodeInfo, b: NodeInfo) => (a.name || '').localeCompare(b.name || '');
    const alertScore = (n: NodeInfo) => {
      const a = stats[n.nodeId]?.alerts;
      return a ? a.critical * 1e6 + a.total : -1;
    };
    return [...list].sort((a, b) => {
      switch (sortKey) {
        case 'name': return byName(a, b);
        case 'alerts': return alertScore(b) - alertScore(a) || byName(a, b);
        case 'lastSeen': return (b.lastSeen || 0) - (a.lastSeen || 0) || byName(a, b);
        default: return Number(b.status === 'online') - Number(a.status === 'online') || byName(a, b);
      }
    });
  }, [preStatus, statusFilter, sortKey, stats]);

  const activeFilters = [search.trim(), statusFilter !== 'all', attentionOnly].filter(Boolean).length;
  const clearFilters = () => { setSearch(''); setStatusFilter('all'); setAttentionOnly(false); };

  const handleRegisterNode = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterLoading(true);
    setRegisterError('');
    setRegisterSuccess('');
    try {
      await registerNode(registerForm.nodeId, registerForm.name, registerForm.authToken);
      // Wait for the node list to refresh so the whole registration flow is done
      // before we surface success — no flash of the editable form in between.
      await fetchNodes();
      setRegisterForm({ nodeId: '', name: '', authToken: '' });
      setRegisterSuccess(`Site "${registerForm.name}" registered successfully!`);
      // Keep the loader/disabled state right up until the modal closes.
      setTimeout(() => {
        setShowRegisterModal(false);
        setRegisterSuccess('');
        setRegisterLoading(false);
      }, 1500);
    } catch (err: any) {
      setRegisterError(err.message || 'Failed to register site');
      setRegisterLoading(false);
    }
  };

  const generateId = () => {
    const id = crypto.randomUUID?.() || Math.random().toString(36).substring(2, 15);
    setRegisterForm(prev => ({ ...prev, nodeId: id }));
  };

  const generateToken = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let token = '';
    for (let i = 0; i < 48; i++) token += chars.charAt(Math.floor(Math.random() * chars.length));
    setRegisterForm(prev => ({ ...prev, authToken: token }));
  };

  const handleEditNode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editNode) return;
    setEditLoading(true);
    setEditError('');
    try {
      await updateNode(editNode.nodeId, {
        name: editForm.name,
        authToken: editForm.authToken || undefined,
      });
      setEditNode(null);
      fetchNodes();
    } catch (err: any) {
      setEditError(err.message || 'Failed to update site');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteNode = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await deleteNode(deleteTarget.nodeId);
      setDeleteTarget(null);
      fetchNodes();
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete site');
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEditModal = (node: NodeInfo) => {
    setEditForm({ name: node.name, authToken: '' });
    setEditError('');
    setEditNode(node);
  };

  const openDeleteModal = (node: NodeInfo) => {
    setDeleteError('');
    setDeleteTarget(node);
  };

  const openRegister = () => { setShowRegisterModal(true); setRegisterError(''); setRegisterSuccess(''); };

  const actionProps = (node: NodeInfo) => ({
    onOpen: () => openSite(node),
    onEdit: canEdit ? () => openEditModal(node) : undefined,
    onDelete: canDelete ? () => openDeleteModal(node) : undefined,
  });

  return (
    <div>
      <PageTitle
        title="Dashboard"
        subtitle={loading ? undefined : `${nodes.length} site${nodes.length === 1 ? '' : 's'} · ${onlineCount} online`}
      >
        <div className="flex rounded-lg border border-border bg-card p-0.5" role="group" aria-label="View">
          {([['list', ListBulletIcon, 'List'], ['cards', Squares2X2Icon, 'Cards']] as const).map(([key, Icon, label]) => (
            <button
              key={key}
              onClick={() => changeView(key)}
              aria-pressed={view === key}
              title={`${label} view`}
              className={`flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                view === key ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
        <button
          onClick={refresh}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
          title="Refresh"
        >
          <ArrowPathIcon className="h-4 w-4" />
        </button>
        {hasPermission('nodes.register') && (
          <button
            onClick={openRegister}
            className="bg-brand hover:bg-brand-dark text-white text-xs font-medium h-8 px-3 rounded-lg transition-colors flex items-center gap-1.5"
          >
            <PlusIcon className="h-4 w-4" />
            Register Site
          </button>
        )}
      </PageTitle>

      <PageWithFilters
        filters={(
          <FilterColumn
            open={filterCol.open}
            onToggle={filterCol.toggle}
            label="Site filters"
            activeCount={activeFilters}
            onClearAll={clearFilters}
          >
            <FilterSearch value={search} onChange={setSearch} placeholder="Name, site ID or version" />
            <FilterSection title="Status">
              <div role="radiogroup" className="space-y-0.5">
                {(['all', 'online', 'offline'] as const).map((s) => (
                  <FilterCheck
                    key={s}
                    radio
                    checked={statusFilter === s}
                    onClick={() => setStatusFilter(s)}
                    label={s === 'all' ? 'All' : s === 'online' ? 'Online' : 'Offline'}
                    swatch={s === 'online' ? 'var(--success)' : s === 'offline' ? 'var(--destructive)' : null}
                    count={statusCounts[s]}
                  />
                ))}
              </div>
            </FilterSection>
            <FilterSection title="Attention">
              <FilterCheck
                checked={attentionOnly}
                onClick={() => setAttentionOnly((v) => !v)}
                label="Needs attention"
                count={attentionCount}
              />
              <p className="px-2 text-[11px] text-muted-foreground">Active alerts or offline cameras.</p>
            </FilterSection>
            <FilterSection title="Sort by">
              <div role="radiogroup" className="space-y-0.5">
                {SORTS.map((s) => (
                  <FilterCheck key={s.key} radio checked={sortKey === s.key} onClick={() => setSortKey(s.key)} label={s.label} />
                ))}
              </div>
            </FilterSection>
          </FilterColumn>
        )}
      >
        {error && (
          <div className="rounded-lg border border-border px-4 py-2 text-[13px]" style={tint('var(--destructive)')}>
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-brand"></div>
            <span className="ml-3 text-muted-foreground text-[13px]">Loading sites…</span>
          </div>
        ) : nodes.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>
            </div>
            <p className="text-lg mb-2 text-foreground">No sites registered</p>
            <p className="text-[13px] mb-6">Register a TRINETRA site to get started</p>
            {hasPermission('nodes.register') && (
              <button
                onClick={openRegister}
                className="bg-brand hover:bg-brand-dark text-white font-medium px-6 py-2.5 rounded-lg transition-colors inline-flex items-center gap-2 text-[13px]"
              >
                <PlusIcon className="h-5 w-5" />
                Register Your First Site
              </button>
            )}
          </div>
        ) : visible.length === 0 ? (
          <div className={`${RESULTS_CARD} py-16 text-center text-[13px] text-muted-foreground`}>
            No sites match these filters.{' '}
            <button onClick={clearFilters} className="text-brand hover:underline">Clear filters</button>
          </div>
        ) : view === 'list' ? (
          <div className={RESULTS_CARD}>
            <div className="overflow-x-auto">
              <table className={TABLE}>
                <thead>
                  <tr className={THEAD_ROW}>
                    <th className={TH}>Site</th>
                    <th className={TH}>Status</th>
                    <th className={TH}>Cameras</th>
                    <th className={TH}>Active alerts</th>
                    <th className={TH}>Version</th>
                    <th className={TH}>Last seen</th>
                    <th className={`${TH} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((node) => {
                    const online = node.status === 'online';
                    const s = online ? stats[node.nodeId] : undefined;
                    return (
                      <tr key={node.nodeId} onClick={() => openSite(node)} className={`${ROW} cursor-pointer hover:bg-muted`}>
                        <td className={TD}>
                          <div className="font-medium text-foreground">{node.name}</div>
                          <div className="font-mono text-[11px] text-muted-foreground">{node.nodeId}</div>
                        </td>
                        <td className={TD}><StatusPill online={online} /></td>
                        <td className={`${TD} tabular-nums`}><CamerasValue online={online} stats={s} /></td>
                        <td className={`${TD} tabular-nums`}><AlertsValue online={online} stats={s} /></td>
                        <td className={`${TD} tabular-nums text-muted-foreground`}>{node.version ? `v${node.version}` : '—'}</td>
                        <td className={`${TD} whitespace-nowrap text-muted-foreground`}>{online ? 'now' : formatLastSeen(node.lastSeen)}</td>
                        <td className={`${TD} text-right`}><SiteActions {...actionProps(node)} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {visible.map((node) => {
              const online = node.status === 'online';
              const s = online ? stats[node.nodeId] : undefined;
              return (
                <div
                  key={node.nodeId}
                  onClick={() => openSite(node)}
                  className="relative cursor-pointer overflow-hidden rounded-xl border border-border bg-card p-3 pl-4 shadow-card transition-colors hover:border-brand"
                >
                  {/* Status accent, so the grid scans from the left edge. */}
                  <span className="absolute inset-y-0 left-0 w-1" style={{ backgroundColor: online ? 'var(--success)' : 'var(--destructive)' }} aria-hidden />
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-[14px] font-semibold text-foreground" title={node.name}>{node.name}</div>
                      <div className="truncate font-mono text-[11px] text-muted-foreground" title={node.nodeId}>{node.nodeId}</div>
                    </div>
                    <StatusPill online={online} />
                  </div>
                  <dl className="mt-3 grid grid-cols-3 gap-2 text-[12px]">
                    <div>
                      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">Cameras</dt>
                      <dd className="tabular-nums text-foreground"><CamerasValue online={online} stats={s} /></dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">Alerts</dt>
                      <dd className="tabular-nums text-foreground"><AlertsValue online={online} stats={s} /></dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">Last seen</dt>
                      <dd className="text-foreground">{online ? 'now' : formatLastSeen(node.lastSeen)}</dd>
                    </div>
                  </dl>
                  <div className="mt-3 flex items-center justify-between gap-2 border-t border-border pt-2">
                    <span className="text-[11px] tabular-nums text-muted-foreground">{node.version ? `v${node.version}` : 'Version —'}</span>
                    <SiteActions {...actionProps(node)} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </PageWithFilters>

      {/* Register Site Modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl w-full max-w-md border border-border shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">Register Site</h2>
              <button
                onClick={() => setShowRegisterModal(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleRegisterNode} className="px-6 py-5 space-y-4">
              {registerError && (
                <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm px-3 py-2 rounded-lg">
                  {registerError}
                </div>
              )}
              {registerSuccess && (
                <div className="bg-[var(--success)]/10 border border-[var(--success)]/30 text-[var(--success)] text-sm px-3 py-2 rounded-lg">
                  {registerSuccess}
                </div>
              )}

              {/* Site ID */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Site ID</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={registerForm.nodeId}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, nodeId: e.target.value }))}
                    placeholder="e.g., site-office-01"
                    required
                    className="flex-1 bg-input border border-border text-foreground text-sm rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-brand focus:border-transparent outline-none placeholder-muted-foreground"
                  />
                  <button
                    type="button"
                    onClick={generateId}
                    className="bg-muted hover:bg-accent text-foreground text-xs px-3 py-2.5 rounded-lg transition-colors whitespace-nowrap"
                  >
                    Generate
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Unique identifier for this site. Must match the Site ID set on the machine itself.</p>
              </div>

              {/* Site Name */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Site Name</label>
                <input
                  type="text"
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Main Office NVR"
                  required
                  className="w-full bg-input border border-border text-foreground text-sm rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-brand focus:border-transparent outline-none placeholder-muted-foreground"
                />
                <p className="text-xs text-muted-foreground mt-1">A friendly display name for the site.</p>
              </div>

              {/* Auth Token */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Auth Token</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={registerForm.authToken}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, authToken: e.target.value }))}
                    placeholder="Shared secret for site authentication"
                    required
                    className="flex-1 bg-input border border-border text-foreground text-sm rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-brand focus:border-transparent outline-none placeholder-muted-foreground font-mono"
                  />
                  <button
                    type="button"
                    onClick={generateToken}
                    className="bg-muted hover:bg-accent text-foreground text-xs px-3 py-2.5 rounded-lg transition-colors whitespace-nowrap"
                  >
                    Generate
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">This token must also be entered on the machine, under Settings → Cloud Connectivity.</p>
              </div>

              {/* Info box */}
              <div className="bg-[var(--info)]/10 border border-[var(--info)]/30 rounded-lg p-3">
                <p className="text-xs text-[var(--info)] leading-relaxed">
                  <strong>Next step:</strong> On the machine itself, open <code className="bg-[var(--info)]/20 px-1 rounded">Settings → Cloud Connectivity</code> and enter this Site ID and Auth Token to connect it to the cloud.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowRegisterModal(false)}
                  className="flex-1 bg-muted hover:bg-accent text-foreground font-medium py-2.5 rounded-lg transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={registerLoading}
                  className="flex-1 bg-brand hover:bg-brand-dark disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                >
                  {registerSuccess ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      Registered
                    </>
                  ) : registerLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white" />
                      Registering...
                    </>
                  ) : (
                    'Register Site'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Site Modal */}
      {editNode && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl w-full max-w-md border border-border shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">Edit Site</h2>
              <button onClick={() => setEditNode(null)} className="text-muted-foreground hover:text-foreground transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <form onSubmit={handleEditNode} className="px-6 py-5 space-y-4">
              {editError && (
                <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm px-3 py-2 rounded-lg">{editError}</div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Site ID</label>
                <input
                  type="text"
                  value={editNode.nodeId}
                  disabled
                  className="w-full bg-muted border border-border text-muted-foreground text-sm rounded-lg px-3 py-2.5 cursor-not-allowed font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">Site ID cannot be changed.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Site Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="w-full bg-input border border-border text-foreground text-sm rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-brand focus:border-transparent outline-none placeholder-muted-foreground"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Auth Token <span className="text-muted-foreground font-normal">(leave blank to keep current)</span></label>
                <input
                  type="text"
                  value={editForm.authToken}
                  onChange={(e) => setEditForm(prev => ({ ...prev, authToken: e.target.value }))}
                  placeholder="Enter new token to change"
                  className="w-full bg-input border border-border text-foreground text-sm rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-brand focus:border-transparent outline-none placeholder-muted-foreground font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">If changed, update the machine's Cloud Connectivity settings to match.</p>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditNode(null)} className="flex-1 bg-muted hover:bg-accent text-foreground font-medium py-2.5 rounded-lg transition-colors text-sm">Cancel</button>
                <button type="submit" disabled={editLoading} className="flex-1 bg-brand hover:bg-brand-dark disabled:bg-brand/50 disabled:cursor-not-allowed text-foreground font-medium py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2">
                  {editLoading ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white" />Saving...</>
                  ) : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Site Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl w-full max-w-sm border border-border shadow-2xl">
            <div className="px-6 py-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-destructive" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Delete Site</h2>
                  <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
                </div>
              </div>

              <p className="text-sm text-foreground mb-1">
                Are you sure you want to delete <span className="font-semibold text-foreground">{deleteTarget.name}</span>?
              </p>
              <p className="text-xs text-muted-foreground mb-4 font-mono">{deleteTarget.nodeId}</p>

              {deleteTarget.status === 'online' && (
                <div className="bg-[var(--warning)]/10 border border-[var(--warning)]/30 rounded-lg p-3 mb-4">
                  <p className="text-xs text-[var(--warning)]">⚠ This site is currently online. Deleting it will disconnect the tunnel and it will no longer be able to connect.</p>
                </div>
              )}

              {deleteError && (
                <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm px-3 py-2 rounded-lg mb-4">{deleteError}</div>
              )}

              <div className="flex gap-3">
                <button onClick={() => setDeleteTarget(null)} className="flex-1 bg-muted hover:bg-accent text-foreground font-medium py-2.5 rounded-lg transition-colors text-sm">Cancel</button>
                <button onClick={handleDeleteNode} disabled={deleteLoading} className="flex-1 bg-destructive hover:bg-destructive/90 disabled:bg-destructive/50 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2">
                  {deleteLoading ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white" />Deleting...</>
                  ) : 'Delete Site'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Cells shared by the list and card views ─── */

/** Dot + word, so state never depends on colour alone. */
function StatusPill({ online }: { online: boolean }) {
  const c = online ? 'var(--success)' : 'var(--destructive)';
  return (
    <span
      className="inline-flex flex-none items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
      style={tint(c)}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${online ? 'animate-pulse' : ''}`} style={{ backgroundColor: c }} aria-hidden />
      {online ? 'Online' : 'Offline'}
    </span>
  );
}

const Pending = () => <span className="inline-block h-3 w-10 animate-pulse rounded bg-muted align-middle" aria-label="Loading" />;

function CamerasValue({ online, stats }: { online: boolean; stats?: SiteStats }) {
  if (!online) return <span className="text-muted-foreground">—</span>;
  if (!stats || stats.loading) return <Pending />;
  if (!stats.cameras) return <span className="text-muted-foreground">—</span>;
  const { online: on, total } = stats.cameras;
  return (
    <span title={`${on} of ${total} cameras online`}>
      <span style={on < total ? { color: 'var(--warning)' } : undefined}>{on}</span>
      <span className="text-muted-foreground">/{total}</span>
    </span>
  );
}

function AlertsValue({ online, stats }: { online: boolean; stats?: SiteStats }) {
  if (!online) return <span className="text-muted-foreground">—</span>;
  if (!stats || stats.loading) return <Pending />;
  if (!stats.alerts) return <span className="text-muted-foreground">—</span>;
  const { total, critical } = stats.alerts;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={total === 0 ? 'text-muted-foreground' : ''}>{total}</span>
      {critical > 0 && (
        <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold" style={tint('var(--destructive)')}>
          {critical} critical
        </span>
      )}
    </span>
  );
}

/** Icon buttons; every click stops propagation so it doesn't also open the site. */
function SiteActions({ onOpen, onEdit, onDelete }: { onOpen: () => void; onEdit?: () => void; onDelete?: () => void }) {
  const btn = 'rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground';
  const stop = (fn: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn(); };
  return (
    <span className="inline-flex items-center gap-0.5">
      <button onClick={stop(onOpen)} className={btn} title="Open site dashboard" aria-label="Open site dashboard">
        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
      </button>
      {onEdit && (
        <button onClick={stop(onEdit)} className={btn} title="Edit site" aria-label="Edit site">
          <PencilSquareIcon className="h-4 w-4" />
        </button>
      )}
      {onDelete && (
        <button
          onClick={stop(onDelete)}
          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-destructive"
          title="Delete site"
          aria-label="Delete site"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      )}
    </span>
  );
}
